import { fakePackRepack } from 'constants/fakePackRepack';
import React, { useRef, forwardRef, useCallback, useEffect } from 'react';
import { useBreakpoint } from '@landr/maestro';
import { Section } from 'components/Section/Section';
import { useTrackingContext } from 'contexts/TrackingContext/TrackingContext';
import Artworks from 'components/Artworks/Artworks';
import { useGradientContext } from 'controllers/GradientController/GradientController';
import { PlayerProvider } from 'contexts/PlayerContext/PlayerContext';
import { useNavigate } from 'react-router-dom';
import { StreamEntityType } from 'types/enums';
import { SlideProps, SlideTypes, SlideWithFallbackProps } from '../types';
import useSlide from '../hooks/useSlide';
import { getStreamEntityType } from '../helpers/getStreamEntityType';
import { useHeroCarouselContext } from '../context/HeroCarouselContext';
import Details from './Details';
import SlideContentWrapper from './SlideContentWrapper';
import { ArtworksWrapper } from './styled';
import { useCallToAction } from './CallToActions';

export const _Slide = forwardRef(
    (
        {
            isVertical,
            isLoading,
            artworks,
            details,
            handleArtworkClicked,
        }: SlideWithFallbackProps,
        forwardedRef:
            | ((instance: HTMLElement | null) => void)
            | React.MutableRefObject<HTMLElement | null>
            | null,
    ) => (
        <Section
            ref={forwardedRef}
            minHeight="sixty"
            boxed={isVertical ? 'sm' : undefined}
        >
            <Section.Content>
                <SlideContentWrapper isVertical={isVertical}>
                    <Details details={details} isLoading={isLoading} />
                    <ArtworksWrapper
                        onClick={handleArtworkClicked}
                        data-testid="slide-artworks"
                    >
                        <Artworks
                            artworks={artworks}
                            imageSize={
                                details?.type === SlideTypes.Custom
                                    ? [450, 365]
                                    : 500
                            }
                        />
                    </ArtworksWrapper>
                </SlideContentWrapper>
            </Section.Content>
        </Section>
    ),
);

const observerOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.6,
};

const Slide = (slide: SlideProps) => {
    const slideRef = useRef<HTMLElement>(null);
    const { updateGradient } = useGradientContext();
    const isVertical = useBreakpoint('md');
    const {
        getPackPlayButtonClickTrackerV2,
        getCustomHeroSlidePlayButtonClickTrackerV2,
    } = useTrackingContext();
    const { isLoading, artworks, details, backgroundColor, foregroundColor } =
        useSlide(
            slide,
            getPackPlayButtonClickTrackerV2,
            getCustomHeroSlidePlayButtonClickTrackerV2,
        );
    const navigate = useNavigate();

    const { disableAutoPlay, enableAutoPlay } = useHeroCarouselContext();
    const { handleViewClicked, viewCtaLink } = useCallToAction({
        type: details?.type,
        slug: details?.slug,
        customExternalUrl: details?.customExternalUrl,
        isFromArtwork: true,
        disableAutoPlay,
        enableAutoPlay,
    });

    const handleArtworkClicked = useCallback(() => {
        handleViewClicked();
        details?.type === SlideTypes.Custom
            ? window.open(viewCtaLink, '_blank')
            : navigate({
                  pathname: viewCtaLink,
              });
    }, [details?.type, handleViewClicked, navigate, viewCtaLink]);

    const handleObservation = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            if (entries[0].isIntersecting) {
                updateGradient(backgroundColor, foregroundColor);
            }
        },
        [updateGradient, backgroundColor, foregroundColor],
    );

    useEffect(() => {
        const observer = new IntersectionObserver(
            handleObservation,
            observerOptions,
        );

        const slideRefCurrent = slideRef.current;

        slideRefCurrent && observer.observe(slideRefCurrent);

        return () => {
            slideRefCurrent && observer.unobserve(slideRefCurrent);
        };
    }, [handleObservation]);

    return (
        <PlayerProvider
            playerId={details?.demoGuid ?? fakePackRepack.guid}
            streamEntityType={
                details?.type
                    ? getStreamEntityType(details?.type)
                    : StreamEntityType.Packs
            }
            customStreamUrl={details?.demoUrl}
        >
            <_Slide
                ref={slideRef}
                isVertical={isVertical}
                isLoading={isLoading}
                artworks={artworks}
                details={details}
                backgroundColor={backgroundColor}
                foregroundColor={foregroundColor}
                handleArtworkClicked={handleArtworkClicked}
            />
        </PlayerProvider>
    );
};

export default Slide;
